import Head from 'next/head'
import Script from 'next/script'
import Image from 'next/image'
import { s3Static } from '../../../utils/constants'
import { getArticleDateTime, reviewedIso } from '../../../utils/article-date-time'
import { BlogsList } from '../../../components/blogs/BlogList'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getRequestJSON } from '../../../api/index/location-api'
import { airconCostCalculation } from '../../../js/aircon-cost'
import { RequestProvider } from '../../../context/RequestContext'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { Services } from '../../../components/index/service/Services'
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { getWpNewPostsByCategory, getWpPostsByCategory } from '../../../lib/service'
import { getWpHvacServices, getWpNewHvacServices, setWpHvacServices, setWpNewHvacServices } from '../../../service/wp-blogs-store'
import { CassetteAirconBooking } from '../../../components/index/service-category/aircon-service/CassetteAirconBooking'
import { AirconItemSelection } from '../../../components/index/service-category/aircon-service/AirconItemSelection'
import { AirconScope } from '../../../components/index/service-category/aircon-service/AirconScope'
import { openModal } from '../../../service/modal-service'
import { AnimatePresence } from 'framer-motion'
import { AirconCustomerReviews } from '../../../components/index/service-category/aircon-service/AirconCustomerReviews'
import { getTestimonials } from 'api/user/profile-api'
import { getAcTestimonials, setAcTestimonials } from 'service/testimonial-store'
import { buildAirconServiceGraph } from 'utils/aircon-page-schema'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-09-22'

export async function getStaticProps() {
    const countryCd = "sg"
    const countryName = "Singapore"
    
    const allRequestCategorySettings = await getRequestJSON(countryName)

    const testimonials = getAcTestimonials().length > 0 ? getAcTestimonials() : await getTestimonials("Aircon Services", 0, 6)
    setAcTestimonials(testimonials)

    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Aircon Services")
    
    const lat = 1.3521
    const lng = 103.8198

    const { month, year } = getArticleDateTime();
    const modifiedDate = reviewedIso(CONTENT_REVIEWED);
    const airconBlogs = blogsMaster['hvac-services'].blogArray;
    const wpPosts = getWpHvacServices().length > 0 ?  getWpHvacServices() : await getWpPostsByCategory(6, "hvac-services");
    setWpHvacServices(wpPosts)
    const wpNewPosts = getWpNewHvacServices().length > 0 ?  getWpNewHvacServices() : await getWpNewPostsByCategory(6, "hvac-services");
    setWpNewHvacServices(wpNewPosts)

    const currencyCd = "SGD"

    let standardServicing = []
    let annualServicing = []
    let chemicalServicing = []
    let overhaulServicing = []    

    airconCostCalculation(currencyCd, annualServicing, standardServicing, chemicalServicing, overhaulServicing)

  return {
      props: {
        requestCategorySettings,
        modifiedDate,
        month,
        year,
        airconBlogs,
        standardServicing,
        annualServicing,
        currencyCd,
        testimonials,
        countryName,
        wpNewPosts,
        wpPosts
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

export default function Home(props) {
    const router = useRouter()
    const modifiedDate = props.modifiedDate
    const month = props.month
    const year = props.year
    const airconBlogs = props.airconBlogs
    const requestCategorySettings = props.requestCategorySettings;
    const standardServicing = props.standardServicing
    const annualServicing = props.annualServicing
    const currencyCd = props.currencyCd
    const testimonials = props.testimonials
    const requestType = 'Aircon Services'
    const countryName = props.countryName
    const wpPosts = props.wpPosts
    const wpNewPosts = props.wpNewPosts

    const maxRating = [1, 2, 3, 4, 5]
    const [ requestForm, setRequestForm ] = useState(router.query.requestForm)
    const [ isQuickBook, setIsQuickBook ] = useState(false)
    const [ isCustomBook, setIsCustomBook ] = useState(false)
    const [ isShowAirconItemSelection, setIsShowAirconItemSelection ] = useState(false)
    const [ isShowJobScope, setIsShowJobScope ] = useState(false)
    const [ scopeServiceType, setScopeServiceType ] = useState("generalCeiling")
    const [ scopeUnitType, setScopeUnitType ] = useState("")

    const showJobScope = (serviceType, unitType) => {
        setScopeServiceType(serviceType)
        setScopeUnitType(unitType)
        openModal(setIsShowJobScope)
    }

  return (
    <>
      <Head>
        <title>Ceiling Cassette Aircon Servicing in Singapore</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content="Affordable ceiling cassette aircon servicing in Singapore with transparent rates, thorough maintenance process, and tailored service packages for optimal cooling."/>
        <meta name="keywords" content="Ceiling Cassette Aircon Servicing"/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"   content="https://www.stringssg.com/sg/aircon-servicing-singapore/ceiling-cassette-aircon-service" />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content={`Ceiling Cassette Aircon Servicing in Singapore`}/>
        <meta property="og:description" content="Affordable ceiling cassette aircon servicing in Singapore with transparent rates, thorough maintenance process, and tailored service packages for optimal cooling." />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2025-01-29T07:30:46+00:00" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:image"  content="https://stringssg-assets.s3.ap-southeast-1.amazonaws.com/static/blog/living/aircon/ceiling-cassette-aircon-servicing.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3.ap-southeast-1.amazonaws.com/static/blog/living/aircon/ceiling-cassette-aircon-servicing.jpg" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/sg/aircon-servicing-singapore/ceiling-cassette-aircon-service"/>
        <meta name="twitter:title" content={`Ceiling Cassette Aircon Servicing in Singapore`}/> 
        <meta name="twitter:description" content="Affordable ceiling cassette aircon servicing in Singapore with transparent rates, thorough maintenance process, and tailored service packages for optimal cooling."/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/sg/aircon-servicing-singapore/ceiling-cassette-aircon-service"/>
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(buildAirconServiceGraph({
            path: '/sg/aircon-servicing-singapore/ceiling-cassette-aircon-service',
            pageName: 'Ceiling Cassette Aircon Servicing in Singapore',
            description: 'Affordable ceiling cassette aircon servicing in Singapore with transparent rates, thorough maintenance process, and tailored service packages for optimal cooling.',
            serviceName: 'Ceiling Cassette Aircon Servicing Singapore',
            serviceType: 'Aircon Servicing',
            image: 'https://stringssg-assets.s3.ap-southeast-1.amazonaws.com/static/blog/living/aircon/ceiling-cassette-aircon-servicing.jpg',
            datePublished: '2025-01-01T07:30:46+00:00',
            modifiedDate,
          })) }}
        />
        </Head>
        <div>
            <RequestProvider>
                <section id="cassette-aircon-servicing" className="relative">
                    <div className="-mt-14 relative w-full aspect-21/9 sm:h-200 overflow-hidden">
                        <Image className='bg-linear-to-br from-slate-800 to-orange-300'
                            src={s3Static + "blog/living/aircon/ceiling-cassette-aircon-servicing.jpg"}
                            fill
                            style={{objectFit: 'cover'}}
                            alt='Ceiling Cassette Aircon Servicing Singapore'
                            quality={100}
                            priority={true}
                            sizes="(max-width: 768px) 100vw, 33vw"
                        />
                    </div>         
                    <div id="campaign" className='relative bg-gray-100 sm:bg-transparent sm:text-white w-full py-2 sm:absolute sm:top-1/2 sm:-translate-y-1/2'>
                        <CassetteAirconBooking 
                            setRequestForm={setRequestForm} 
                            oneTimeServicing={standardServicing} 
                            setIsQuickBook={setIsQuickBook} 
                            setIsCustomBook={setIsCustomBook} 
                            setIsShowAirconItemSelection={setIsShowAirconItemSelection} 
                            showJobScope={showJobScope} 
                            currencyCd={currencyCd}
                            subtitle="Ensure Optimal Cooling with Professional Ceiling Cassette Aircon Servicing in Singapore"
                        />
                    </div>
                    {requestForm &&
                        <Services requestType={requestType} requestCategorySettings={requestCategorySettings} setRequestForm={setRequestForm} isQuickBook={isQuickBook} isCustomBook={isCustomBook} asSheet/>                    
                    }
                    {/* The sheets are portal-style overlays, so they sit outside the
                        booking-panel / Services ternary. */}
                    {isShowJobScope &&
                        <AnimatePresence>
                            <AirconScope
                                setIsShowJobScope={setIsShowJobScope}
                                serviceType={scopeServiceType}
                                unitType={scopeUnitType} />
                        </AnimatePresence>
                    }
                    {isShowAirconItemSelection &&
                        <AnimatePresence>
                            <AirconItemSelection setIsShowAirconItemSelection={setIsShowAirconItemSelection} setRequestForm={setRequestForm} setIsCustomBook={setIsCustomBook} setIsQuickBook={setIsQuickBook} userId={null}/>
                        </AnimatePresence>
                    }
                </section>
            </RequestProvider>
            <div className='max-w-4xl mx-auto py-10 px-2 leading-8'>
                <div className='space-y-5'>
                    <h2 className='text-3xl md:text-4xl font-semibold text-center'>Ceiling Cassette Aircon Servicing in Singapore</h2>
                    {/* <div className='text-gray-400 text-xs'>
                        <time dateTime={modifiedDate}>Updated - {new Date(modifiedDate).toUTCString().substring(0,16)}</time>
                    </div> */}
                    <p>
                        Ceiling cassette air conditioners represent a discreet yet powerful cooling solution widely embraced in diverse 
                        environments. From bustling offices to cozy restaurants and expansive residential spaces, these units blend 
                        seamlessly into ceilings while efficiently distributing cool air through multiple vents. Their inconspicuous 
                        design doesn&apos;t compromise on performance, but like any air conditioning system, they require regular servicing 
                        to function optimally.
                    </p>
                    <p>
                        Cassette air conditioners are the go-to choice for spaces where aesthetics and functionality converge. Their 
                        discreet placement and even air distribution make them ideal for various industries and larger homes. Regular 
                        servicing is key to maintaining their efficiency, preserving air quality, and extending their lifespan.
                    </p>
                </div>      
                <div className='max-w-5xl mx-auto my-20 space-y-5'>
                    <h2 className='font-bold text-2xl md:text-3xl text-center uppercase'>Ceiling Cassette Aircon Service Pricing</h2>
                    <div className='py-2 px-2 space-y-2 max-w-5xl leading-8 mx-auto'> 
                        <p className=''>
                            Navigating through numerous AC servicing companies to find the perfect match for your needs and budget can be overwhelming. 
                            Simplifying your search, we present a comprehensive pricing structure, delivering affordable aircon servicing in Singapore.
                        </p> 
                        <div className='grid md:grid-cols-2 py-5 gap-5'>
                            <div>
                                <h3 className='text-gray-400 text-base font-bold text-center'>One-Time Servicing</h3>
                                <div className='flex justify-center'>
                                    <table className='w-full sm:w-5/6'>
                                        <thead className='text-orange-600 text-sm border'>
                                            <tr className=''>
                                                <th className='p-2'>No. of Aircons</th>
                                                <th className='p-2'>Pricing</th>
                                            </tr>
                                        </thead>
                                        
                                        <tbody className='text-xs border'>      
                                            {[...standardServicing].map((tran, i) =>                      
                                                <tr key={i} className="border">
                                                    <td className="p-2 text-center whitespace-nowrap border">
                                                        {tran.number}
                                                    </td>
                                                    <td className="p-2 text-center whitespace-nowrap">
                                                        {tran.currencyCd} {(parseFloat(tran.rate) + parseFloat(tran.rate*75/100)).toFixed(2)} 
                                                    </td>
                                                </tr>                           
                                            )} 
                                            <tr className="border">
                                                <td className="p-2 text-center border">
                                                    more than 5
                                                </td>
                                                <td className="p-2 text-center">
                                                    Open request for pricing
                                                </td>
                                            </tr>                    
                                        </tbody>    
                                    </table>
                                </div>
                            </div>
                            <div className=''>
                                <h3 className='text-gray-400 text-base font-bold text-center'>Annual Package</h3>
                                <div className='flex justify-center'>
                                    <table className='w-full sm:w-5/6'>
                                        <thead className='text-orange-600 text-sm border'>
                                            <tr className=''>
                                                <th className='p-2'>No. of Aircons</th>
                                                <th className='p-2'>4 times/year</th>
                                                <th className='p-2'>3 times/year</th>
                                            </tr>
                                        </thead>
                                        
                                        <tbody className='text-xs border'>      
                                            {[...annualServicing].map((tran, i) =>                      
                                                <tr key={i} className="border">
                                                    <td className="p-2 text-center whitespace-nowrap border">
                                                        {tran.number}
                                                    </td>
                                                    <td className="p-2 text-center whitespace-nowrap border">
                                                        {tran.currencyCd} {(parseFloat(tran.threeMonthInterval) + parseFloat(tran.threeMonthInterval*75/100)).toFixed(2)}                                                
                                                    </td>
                                                    <td className="p-2 text-center whitespace-nowrap border">
                                                        {tran.currencyCd} {(parseFloat(tran.fourMonthInterval) + parseFloat(tran.fourMonthInterval*75/100)).toFixed(2)}
                                                    </td>
                                                </tr>                           
                                            )} 
                                            <tr className="border">
                                                <td className="p-2 text-center border">
                                                    more than 5
                                                </td>
                                                <td className="p-2 text-center border">
                                                    Open request for pricing
                                                </td>
                                                <td className="p-2 text-center">
                                                    Open request for pricing
                                                </td>
                                            </tr>                   
                                        </tbody>    
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>  
                <div className='relative w-full h-96'>
                    <Image
                        src={s3Static + "blog/living/aircon/ceiling-aircon.jpg"}
                        fill
                        style={{objectFit: 'cover', objectPosition: 'center'}}  
                        placeholder='blur'
                        blurDataURL={s3Static + "placeholder.png"}
                        alt='Ceiling A/C Unit'
                    />          
                </div>                
                <div className='max-w-4xl mx-auto py-10'>
                    <h2 className='text-xl md:text-2xl text-center uppercase'>
                        <strong>Cassette Aircon Servicing Process in Steps</strong>
                    </h2>
                    <div className='mt-10 text-sm leading-7 space-y-5'>                
                        <p className='text-center font-bold text-lg'>
                            StringsSG takes a meticulous approach to servicing your cassette air conditioning system.
                        </p>
                        <div className='bg-slate-200 px-2 py-5 text-center h-full'>
                            <h3 className='text-base'><strong>1. Initial Assessment</strong></h3>                            
                            <p className='py-5'>
                                Our skilled technicians conduct a thorough assessment of your system, identifying any visible issues or areas requiring attention.
                            </p> 
                        </div>
                        <div className='px-2 py-5 text-center h-full'>
                            <h3 className='text-base'><strong>2. Cleaning and Maintenance Steps</strong></h3>
                            <div className='py-5'>
                                We meticulously clean filters, coils, and other critical components, ensuring optimal airflow and system efficiency. Lubrication or 
                                part replacement is undertaken if necessary, ensuring peak performance.
                            </div> 
                        </div>
                        <div className='bg-slate-200  px-2 py-5 text-center h-full'>
                            <h3 className='text-base'><strong>3. Testing and Quality Check</strong></h3>
                            <div className='py-5'>
                                We rigorously test the system post-servicing to ensure it operates at its best, guaranteeing efficient cooling and longevity.
                            </div> 
                        </div>
                        <div className='px-2 py-5 text-center h-full'>
                            <h3 className='text-base'><strong>4. Monitor Refrigerant Gas Level</strong></h3>
                            <p className='py-5'>
                                Refrigerant gas acts as the cooling agent for the aircon, if the gas level is too low or empty there may be no cooling effect. The technician
                                will use a device to check the gas level and inform you if it needs a refill.
                            </p> 
                        </div>
                        <p>
                        To extend the life of your system, our experts provide valuable tips and recommendations for simple maintenance routines that you can perform between 
                        professional servicing.
                        </p>
                    </div>
                </div>                          
                {/* <div className='py-10 max-w-5xl mx-auto space-y-2'>
                    <h3 className='text-xl px-2 text-center'><strong>Testimonials</strong></h3>
                    <p className='text-center px-2 pb-5'>Hear what people have to say about us.</p>
                    <Carousel autoPlay={true} infiniteLoop={true} showThumbs={false} showStatus={false} interval={5000} preventMovementUntilSwipeScrollTolerance={true} swipeScrollTolerance={50}
                        className="border-2 shadow shadow-gray-300 rounded-xl">
                        {[...testimonials].map((review, i) =>
                            <div key={i} className="p-4 text-sm leading-6 space-y-2">
                                <h4 className='text-center font-bold'>
                                    {review.requestUserFullNm}
                                </h4>
                                <div className='flex justify-center items-center'>
                                    <div className={'text-xs text-white px-2 py-1 rounded-full ' + 
                                            (review.rating >= 4 && " bg-green-600 ") +
                                            ((review.rating >= 3 && review.rating < 4) && " bg-blue-600 ") +
                                            ((review.rating >= 2 && review.rating < 3) && " bg-cyan-600 ") +
                                            ((review.rating >= 1 && review.rating < 2) && " bg-amber-500 ") +
                                            ((review.rating > 0 && review.rating < 1) && " bg-red-600 ")                             
                                        }                              
                                        >
                                        {review.rating}
                                    </div>
                                    <div className="px-1 py-3 flex space-x-1 text-lg">  
                                        {[...maxRating].map((star, x) =>                             
                                            <div key={x} className="relative">
                                                <FaStar className=' text-gray-300'/>
                                                {x < review.rating  &&
                                                <div className=''>
                                                    <FaStar className='text-amber-300' style={{marginTop: "-18px"}}/>
                                                </div>
                                                }
                                            </div>                        
                                        )}
                                    </div>
                                </div>
                                <p className='pb-10 px-4 break-words whitespace-pre-wrap max-h-80 overflow-y-auto scrollbar-hide'>
                                    {review.reviewComment}
                                </p>
                            </div>
                        )}
                    </Carousel>
                </div>                  */}
            </div>
            <AirconCustomerReviews testimonials={testimonials} />
            <BlogsList blogs={airconBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts}/>
            {/* <AirconQuickLinks/> */}
        </div>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
        />
        <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
